/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import cx from 'classnames';
import styles from './styles.module.scss';
import { FlexGrid } from '@/components/FlexGrid/FlexGrid';
import { Actionable } from '@az/starc-ui';
import Image from 'src/components/NextImage';
import { ReviewProduct } from './ReviewProduct/ReviewProduct';
import { type ProductCardProps } from './interface';
import { useActionCandidates } from '../../context/ActionCandidatesProvider';
import { BuyAgainProduct } from './BuyAgainProduct/BuyAgainProduct';
import { type ElementType, useCallback } from 'react';
import { useForteDecisionFlagWithTrueDefault } from '@/features/forte/api/getForteDecision';
import { usePageSpecificData } from '@/utils/analytics/usePageSpecificData';
import { usePageType } from '@/hooks/usePageType';
import { sessionStorage } from '@/utils/sessionStorage';
import { pageTypes } from '@/constants/page';
import { analyticsPageType } from '@/constants/analytics/analyticsPageType';

export const ProductCard = ({ reviewCandidate, buyAgainCandidate, formData }: ProductCardProps) => {
  const { isReviewCandidates, isBuyAgainCandidates, isOneProduct } = useActionCandidates();
  const enableBuyAgainRedesign = useForteDecisionFlagWithTrueDefault('enableBuyAgainRedesign');
  const isBuyAgainRedesign = isBuyAgainCandidates && enableBuyAgainRedesign;
  const name = reviewCandidate?.productName ?? buyAgainCandidate?.skuDisplayName;
  const imageUrl = reviewCandidate?.imageUrl ?? buyAgainCandidate?.imageUrl ?? '';

  const { setPageSpecificData } = usePageSpecificData();
  const { pageType } = usePageType();

  const handleBuyAgainAnalytics = useCallback(() => {
    const pfmPageType =
      pageType === pageTypes.ProductShelf ? analyticsPageType.catalogShelf : pageType;
    sessionStorage.setItem('productType', 'buyAgain');
    setPageSpecificData({
      productFindingMethod: 'Product Recommendation',
      productRecommendationType: 'Buy Again',
      ...(pfmPageType && { pfmLocation: `Buy Again: ${pfmPageType}` }),
    });
  }, [pageType, setPageSpecificData]);

  const Wrapper: ElementType = isBuyAgainRedesign ? Actionable : 'div';
  const wrapperProps = isBuyAgainRedesign
    ? { disabled: false, href: buyAgainCandidate?.detailUrl, onClick: handleBuyAgainAnalytics }
    : {};

  return (
    <Wrapper
      {...wrapperProps}
      className={cx({ [styles.group]: !isOneProduct, [styles.cardGrow]: isBuyAgainRedesign })}
    >
      <FlexGrid alignItems="center" className={styles.card}>
        <Image src={imageUrl} alt={name} width={64} height={64} className={styles.productImage} />
        <FlexGrid flexDirection="column" justifyContent="space-between" gap={2}>
          {isReviewCandidates && !!reviewCandidate ? (
            <ReviewProduct product={reviewCandidate} formData={formData} />
          ) : isBuyAgainCandidates && !!buyAgainCandidate ? (
            <BuyAgainProduct buyAgainCandidate={buyAgainCandidate} />
          ) : null}
        </FlexGrid>
      </FlexGrid>
    </Wrapper>
  );
};
